import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useGetFeedPlayerByIdQuery } from '@/api/feed/getFeedPlayerById';
import { Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Breadcrumbs } from '@/components/layout/Breadcrumbs';
import { PageContainer } from '@/components/layout/PageContainer';
import { ContentCard } from '@/components/layout/ContentCard';
import { Skeleton } from '@/components/ui/skeleton';
import { PlayerInformationSection, PlayerStatsSection } from '@/components/forms/player';
import { generateMLBHeadshotUrl, getPlayerAvatarUrl, type Sport } from '@/utils/jersey-mapping';
import { isSuperAdmin } from '@/utils/auth';

export const Route = createFileRoute('/dashboard/sports/$id/players/$playerId')({
  component: RouteComponent,
});

function RouteComponent() {
  const { id, playerId } = Route.useParams();
  const navigate = useNavigate();

  // Convert route id to lowercase for API (NFL -> nfl, NBA -> nba)
  const leagueId = id.toLowerCase();

  const { data: playerData, isLoading } = useGetFeedPlayerByIdQuery(leagueId, playerId);

  const sportName = id.toUpperCase();

  if (isLoading) {
    return (
      <PageContainer>
        <Breadcrumbs
          overrides={{ sports: 'Sports', [id]: id.toUpperCase() }}
          pathOverrides={{ sports: `/dashboard/sports/${id}` }}
        />
        <ContentCard>
          <div className="space-y-4">
            <Skeleton className="h-10 w-64" />
            <Skeleton className="h-64 w-full" />
          </div>
        </ContentCard>
      </PageContainer>
    );
  }

  if (!isLoading && !playerData) {
    return (
      <PageContainer>
        <Breadcrumbs
          overrides={{ sports: 'Sports', [id]: id.toUpperCase() }}
          pathOverrides={{ sports: `/dashboard/sports/${id}` }}
        />
        <ContentCard>
          <div className="text-center py-8">
            <p className="text-gray-600 dark:text-gray-400">Player not found</p>
            <Button
              variant="outline"
              onClick={() => navigate({ to: '/dashboard/sports/$id/players', params: { id } })}
              className="mt-4"
            >
              Back to Players
            </Button>
          </div>
        </ContentCard>
      </PageContainer>
    );
  }

  if (!playerData) {
    return null; // Still loading
  }

  const { player, team } = playerData;

  const headshotUrl =
    leagueId === 'mlb' && player.firstName && player.lastName && team?.abbreviation
      ? generateMLBHeadshotUrl(team.abbreviation, player.firstName, player.lastName)
      : undefined;

  const avatarUrl = team?.abbreviation
    ? (getPlayerAvatarUrl(team.abbreviation, sportName as Sport, player.jerseyNumber) ?? undefined)
    : undefined;

  return (
    <PageContainer>
      <Breadcrumbs
        overrides={{ sports: 'Sports', [id]: id.toUpperCase() }}
        pathOverrides={{ sports: `/dashboard/sports/${id}` }}
      />
      <div className="flex gap-4 items-center mb-6 flex-wrap">
        <div className="flex items-center gap-2">
          <Settings className="h-5 w-5 text-gray-500" />
          <h1 className="text-2xl font-semibold">
            {sportName} {'>'} Players {'>'} {player.displayName}
          </h1>
        </div>

        <div className="flex gap-2 ml-auto">
          <Button
            variant="outline"
            onClick={() => navigate({ to: '/dashboard/sports/$id/players', params: { id } })}
          >
            Cancel
          </Button>
          <Button className="bg-blue-600 hover:bg-blue-700 text-white" disabled>
            Save
          </Button>
        </div>
      </div>

      <div className="space-y-6">
        <PlayerInformationSection
          sportName={sportName}
          teams={[]}
          selectedTeamId={player.teamId}
          name={player.displayName}
          number={player.jerseyNumber}
          position={player.position}
          status="Active"
          headshotUrl={headshotUrl}
          avatarUrl={avatarUrl}
          disabled
          canEditImages={isSuperAdmin()}
          teamAbbreviation={team?.abbreviation}
        />

        <PlayerStatsSection sport={sportName} playerId={playerId} teamId={player.teamId} />
      </div>
    </PageContainer>
  );
}
